import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import StarIcon from "@mui/icons-material/Star";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import TextField from "@mui/material/TextField";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import OverviewTabDisplay from "./tabs/OverviewTabDisplay";
import TeamPerformanceTabDisplay from "./tabs/TeamPerformanceTabDisplay";
import AllSurveysTabDisplay from "./tabs/AllSurveysTabDisplay";
import { API_get_surveys_for_date_range } from "../../../constants";
import { getDefaultAxiosConfig } from "../../../utilities/requests";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { createStoreInfoLookup, groupStoreOptionsByCompany } from "../../../helpers/storeOptions";

export default function GuestReviewsDisplay2(props) {

  const [currentTab, setCurrentTab] = useState(0);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(14); // days
  const [selectedStore, setSelectedStore] = useState(props.homeStore);
  const [searchTerm, setSearchTerm] = useState("");
  const [storeDropdownOpen, setStoreDropdownOpen] = useState(false);
  const storeAnchorRef = useRef(null);

  // Processed analytics data
  const [overallStats, setOverallStats] = useState({});
  const [trendData, setTrendData] = useState([]);
  const [teamStats, setTeamStats] = useState([]);
  const [timePatterns, setTimePatterns] = useState([]);
  const [ratingDistribution, setRatingDistribution] = useState([]);

  // Use selected store instead of props.store_id
  const storeId = selectedStore;

  // Create store info lookup for getting store names
  const storeInfoLookup = useMemo(
    () => createStoreInfoLookup(props.storeNames),
    [props.storeNames]
  );

  const getStoreName = (storeId) => {
    return storeInfoLookup.get(storeId)?.store_name ?? '';
  };

  // Filter and group stores
  const filteredStoreOptions = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    return (props.storeOptions || []).filter((option) => {
      const storeName = getStoreName(option.store);
      if (!storeName) return false;
      if (!search) return true;
      return storeName.toLowerCase().includes(search);
    });
  }, [props.storeOptions, searchTerm, storeInfoLookup]);

  const groupedStoreOptions = useMemo(
    () => groupStoreOptionsByCompany(filteredStoreOptions, storeInfoLookup),
    [filteredStoreOptions, storeInfoLookup]
  );

  const showCompanyLabels = groupedStoreOptions.length > 1;

  // Store dropdown handlers
  const handleStoreChange = (storeId) => {
    setSelectedStore(storeId);
    if (props.change_home_store) {
      props.change_home_store(storeId);
    }
    setStoreDropdownOpen(false);
  };

  const handleStoreDropdownOpen = () => {
    setStoreDropdownOpen(true);
  };

  const handleStoreDropdownClose = () => {
    setStoreDropdownOpen(false);
    setSearchTerm("");
  };

  // Sync selected store when homeStore prop changes
  useEffect(() => {
    if (props.homeStore && props.homeStore !== selectedStore) {
      setSelectedStore(props.homeStore);
    }
  }, [props.homeStore]);

  // Clear search term when dropdown opens/closes
  useEffect(() => {
    setSearchTerm("");
  }, [storeDropdownOpen]);

  // Fetch surveys when store or date range changes
  useEffect(() => {
    if (!storeId) return;

    const fetchSurveys = async () => {
      setLoading(true);
      setError(null);

      try {
        // Calculate date range
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - dateRange);

        // Format dates as YYYY-MM-DD in local timezone (avoid UTC shift from toISOString)
        const formatLocalDate = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const dateStart = formatLocalDate(startDate);
        const dateEnd = formatLocalDate(endDate);

        const axiosConfig = getDefaultAxiosConfig();
        const url = `${API_get_surveys_for_date_range}${storeId}&date_start=${dateStart}&date_end=${dateEnd}`;

        const response = await axios.get(url, axiosConfig);

        setSurveys(response.data.surveys || []);

        // Transform data into analytics metrics
        processAnalytics(response.data.surveys || []);

      } catch (err) {
        setError(err.message);
        console.error('Error fetching surveys:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSurveys();
  }, [storeId, dateRange]);

  // Process analytics from raw survey data
  const processAnalytics = (surveyData) => {
    if (surveyData.length === 0) {
      // Set empty states
      setOverallStats({
        totalSurveys: 0,
        avgRating: 0,
        positiveSurveys: 0,
        negativeSurveys: 0,
        positivePercentage: 0,
        negativePercentage: 0
      });
      setTrendData([]);
      setTeamStats([]);
      setTimePatterns([]);
      setRatingDistribution([
        { name: '5 Stars', value: 0, color: '#22c55e' },
        { name: '4 Stars', value: 0, color: '#84cc16' },
        { name: '3 Stars', value: 0, color: '#eab308' },
        { name: '2 Stars', value: 0, color: '#f97316' },
        { name: '1 Star', value: 0, color: '#ef4444' }
      ]);
      return;
    }

    // 1. Calculate overall stats
    const stats = calculateOverallStats(surveyData);
    setOverallStats(stats);

    // 2. Generate trend data (daily averages)
    const trend = calculateTrendData(surveyData);
    setTrendData(trend);

    // 3. Calculate team member statistics
    const team = calculateTeamStats(surveyData);
    setTeamStats(team);

    // 4. Analyze time patterns
    const time = calculateTimePatterns(surveyData);
    setTimePatterns(time);

    // 5. Rating distribution
    const dist = calculateRatingDistribution(surveyData);
    setRatingDistribution(dist);
  };

  // Helper: Calculate overall statistics
  const calculateOverallStats = (surveys) => {
    const totalSurveys = surveys.length;
    const avgRating = totalSurveys > 0
      ? (surveys.reduce((sum, s) => sum + s.rating, 0) / totalSurveys).toFixed(2)
      : 0;
    const positiveSurveys = surveys.filter(s => s.rating === 5).length;
    const negativeSurveys = surveys.filter(s => s.rating <= 3).length;
    const positivePercentage = totalSurveys > 0
      ? ((positiveSurveys / totalSurveys) * 100).toFixed(1)
      : 0;
    const negativePercentage = totalSurveys > 0
      ? ((negativeSurveys / totalSurveys) * 100).toFixed(1)
      : 0;

    return {
      totalSurveys,
      avgRating,
      positiveSurveys,
      negativeSurveys,
      positivePercentage,
      negativePercentage
    };
  };

  // Helper: Calculate trend data (group by date)
  const calculateTrendData = (surveys) => {
    const dailyData = {};

    surveys.forEach(survey => {
      const date = survey.date;
      if (!dailyData[date]) {
        dailyData[date] = {
          date,
          ratings: [],
          count: 0
        };
      }
      dailyData[date].ratings.push(survey.rating);
      dailyData[date].count++;
    });

    return Object.values(dailyData)
      .map(day => ({
        date: day.date,
        avgRating: parseFloat((day.ratings.reduce((a,b) => a+b, 0) / day.count).toFixed(2)),
        count: day.count,
        positivePercentage: parseFloat(
          ((day.ratings.filter(r => r === 5).length / day.count) * 100).toFixed(1)
        )
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // Helper: Calculate team statistics (group by staff member)
  const calculateTeamStats = (surveys) => {
    const staffData = {};

    surveys.forEach(survey => {
      survey.staff_members.forEach(staff => {
        if (!staffData[staff.id]) {
          staffData[staff.id] = {
            id: staff.id,
            name: staff.name,
            role: staff.role || 'Unknown',
            ratings: [],
            ratingCounts: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
            totalSurveys: 0
          };
        }

        staffData[staff.id].ratings.push(survey.rating);
        staffData[staff.id].totalSurveys++;
        const roundedRating = Math.round(survey.rating);
        if (roundedRating >= 1 && roundedRating <= 5) {
          staffData[staff.id].ratingCounts[roundedRating]++;
        }
      });
    });

    return Object.values(staffData)
      .map(staff => ({
        ...staff,
        avgRating: parseFloat(
          (staff.ratings.reduce((a,b) => a+b, 0) / staff.totalSurveys).toFixed(2)
        ),
        positivePercentage: parseFloat(
          ((staff.ratingCounts[5] / staff.totalSurveys) * 100).toFixed(1)
        ),
        negativePercentage: parseFloat(
          (((staff.ratingCounts[3] + staff.ratingCounts[2] + staff.ratingCounts[1]) / staff.totalSurveys) * 100).toFixed(1)
        ),
        ratings: staff.ratingCounts  // Rename for clarity
      }))
      .sort((a, b) => b.avgRating - a.avgRating);
  };

  // Helper: Calculate time patterns (group by hour)
  const calculateTimePatterns = (surveys) => {
    const hourlyData = {};

    // Initialize all hours 0-23
    for (let hour = 0; hour < 24; hour++) {
      hourlyData[hour] = {
        hour,
        ratings: [],
        count: 0
      };
    }

    surveys.forEach(survey => {
      const hour = Math.floor(survey.decimal_hour);
      if (hour >= 0 && hour < 24) {
        hourlyData[hour].ratings.push(survey.rating);
        hourlyData[hour].count++;
      }
    });

    return Object.values(hourlyData)
      .filter(h => h.count > 0)
      .map(hour => ({
        hour: hour.hour,
        avgRating: parseFloat((hour.ratings.reduce((a,b) => a+b, 0) / hour.count).toFixed(2)),
        count: hour.count,
        positivePercentage: parseFloat(
          ((hour.ratings.filter(r => r === 5).length / hour.count) * 100).toFixed(1)
        ),
        ratings: {
          5: hour.ratings.filter(r => Math.round(r) === 5).length,
          4: hour.ratings.filter(r => Math.round(r) === 4).length,
          3: hour.ratings.filter(r => Math.round(r) === 3).length,
          2: hour.ratings.filter(r => Math.round(r) === 2).length,
          1: hour.ratings.filter(r => Math.round(r) === 1).length
        }
      }));
  };

  // Helper: Calculate rating distribution
  const calculateRatingDistribution = (surveys) => {
    const distribution = [
      { name: '5 Stars', value: 0, color: '#22c55e' },
      { name: '4 Stars', value: 0, color: '#84cc16' },
      { name: '3 Stars', value: 0, color: '#eab308' },
      { name: '2 Stars', value: 0, color: '#f97316' },
      { name: '1 Star', value: 0, color: '#ef4444' }
    ];

    surveys.forEach(survey => {
      const rounded = Math.round(survey.rating);
      if (rounded >= 1 && rounded <= 5) {
        distribution[5 - rounded].value++;
      }
    });

    return distribution;
  };

  const tabs = [
    { id: 0, label: "Overview" },
    { id: 1, label: "Team Performance" },
    // { id: 2, label: "Time Analysis" },
    // { id: 3, label: "Staffing Insights" },
    { id: 4, label: "All Surveys" },
  ];

  // Show loading state
  if (loading) {
    return (
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <LoadSpinner />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Error: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Guest Feedback</h1>
            <div className="flex items-center mt-2">
              <p className="text-gray-600 mr-2">Powered by</p>
              <img
                src="https://rightworkpublic.s3.us-west-1.amazonaws.com/ovation-logo.png"
                alt="Ovation"
                className="h-6"
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Store
              </label>
              {props.storeOptions && props.storeOptions.length > 1 ? (
                <div
                  ref={storeAnchorRef}
                  onClick={handleStoreDropdownOpen}
                  className="flex items-center gap-1 cursor-pointer px-4 py-2 border border-gray-300 rounded-lg hover:border-violet-600 transition-colors bg-white"
                  style={{ minWidth: "200px", height: "42px" }}
                >
                  <span className="flex-1 text-gray-900">{getStoreName(selectedStore)}</span>
                  <div className="text-gray-500">
                    {storeDropdownOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                  </div>
                </div>
              ) : (
                <div
                  className="px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  style={{ minWidth: "200px", height: "42px" }}
                >
                  <span className="text-gray-900">{getStoreName(selectedStore)}</span>
                </div>
              )}
              <Popover
                open={storeDropdownOpen}
                anchorEl={storeAnchorRef.current}
                onClose={handleStoreDropdownClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left"
                }}
              >
                <Box p={2} width="300px">
                  <TextField
                    placeholder="Search stores..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    fullWidth
                    size="small"
                    autoFocus
                    InputProps={{
                      sx: {
                        backgroundColor: "white",
                        borderRadius: "8px",
                        padding: "5px",
                        height: "42px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                      },
                    }}
                  />
                  <List>
                    {groupedStoreOptions.map((group) => (
                      <React.Fragment key={group.companyId || `company-${group.companyName}`}>
                        {showCompanyLabels && (
                          <MenuItem
                            disabled
                            sx={{
                              fontWeight: "bold",
                              opacity: 1,
                              "&.Mui-disabled": {
                                opacity: 1,
                              },
                            }}
                          >
                            {group.companyName}
                          </MenuItem>
                        )}
                        {group.options.map((option) => (
                          <MenuItem
                            key={option.store}
                            onClick={() => handleStoreChange(option.store)}
                            selected={selectedStore === option.store}
                          >
                            {getStoreName(option.store)}
                          </MenuItem>
                        ))}
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              </Popover>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Date Range
              </label>
              <Select
                value={dateRange}
                onChange={(e) => setDateRange(Number(e.target.value))}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "10px",
                  height: "42px",
                  minWidth: "160px",
                  width: "160px",
                  ".MuiOutlinedInput-notchedOutline": {
                    borderWidth: "1px",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                }}
              >
                <MenuItem value={7}>Last 7 days</MenuItem>
                <MenuItem value={14}>Last 14 days</MenuItem>
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={60}>Last 60 days</MenuItem>
                <MenuItem value={90}>Last 90 days</MenuItem>
              </Select>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-6 mt-6">
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-gray-600 text-sm font-medium">Total Surveys</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{overallStats.totalSurveys}</p>
            <p className="text-xs text-gray-500 mt-1">Last {dateRange} days</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <p className="text-gray-600 text-sm font-medium">Average Rating</p>
            <div className="flex items-baseline mt-1">
              <p className="text-3xl font-bold text-gray-900">{overallStats.avgRating}</p>
              <p className="text-gray-500 ml-1">/ 5.0</p>
            </div>
            <div className="flex items-center mt-1">
              {[...Array(5)].map((_, i) => (
                <StarIcon
                  key={i}
                  className={`w-4 h-4 ${
                    i < Math.round(overallStats.avgRating) ? 'text-yellow-400' : 'text-gray-300'
                  }`}
                  sx={{ fontSize: "16px" }}
                />
              ))}
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg shadow border border-green-200">
            <p className="text-green-800 text-sm font-medium">Positive Surveys</p>
            <div className="flex items-baseline mt-1">
              <p className="text-3xl font-bold text-green-900">{overallStats.positiveSurveys}</p>
              <p className="text-green-700 ml-2 text-sm">({overallStats.positivePercentage}%)</p>
            </div>
            <p className="text-xs text-green-600 mt-1">5 star ratings</p>
          </div>
          <div className="bg-red-50 p-4 rounded-lg shadow border border-red-200">
            <p className="text-red-800 text-sm font-medium">Negative Surveys</p>
            <div className="flex items-baseline mt-1">
              <p className="text-3xl font-bold text-red-900">{overallStats.negativeSurveys}</p>
              <p className="text-red-700 ml-2 text-sm">({overallStats.negativePercentage}%)</p>
            </div>
            <p className="text-xs text-red-600 mt-1">1-3 star ratings</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-8">
        <div className="flex space-x-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setCurrentTab(tab.id)}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                currentTab === tab.id
                  ? 'border-violet-600 text-violet-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-8">
        {currentTab === 0 && (
          <OverviewTabDisplay
            surveys={surveys}
            overallStats={overallStats}
            trend={trendData}
            teamStats={teamStats}
            timePatterns={timePatterns}
            ratingDistribution={ratingDistribution}
            dateRange={dateRange}
          />
        )}
        {currentTab === 1 && (
          <TeamPerformanceTabDisplay
            surveys={surveys}
            teamStats={teamStats}
            overallStats={overallStats}
            dateRange={dateRange}
          />
        )}
        {currentTab === 2 && (
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <p className="text-gray-600">Time Analysis tab coming soon...</p>
          </div>
        )}
        {currentTab === 3 && (
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <p className="text-gray-600">Staffing Insights tab coming soon...</p>
          </div>
        )}
        {currentTab === 4 && (
          <AllSurveysTabDisplay
            surveys={surveys}
            dateRange={dateRange}
          />
        )}
      </div>
    </div>
  );
}
